import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../lib/api';
import { writeResilient } from '../../lib/offlineQueue';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  FileText, Brain, CheckCircle, Save, Send, ChevronLeft
} from 'lucide-react';

export default function ClinicalNotesEditor() {
  const { encounterId } = useParams();
  const [note, setNote] = useState({
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    icd_codes: [],
    cpt_codes: []
  });
  const [aiSummary, setAiSummary] = useState('');
  const [saving, setSaving] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [noteId, setNoteId] = useState(null);
  const [encounter, setEncounter] = useState(null);

  useEffect(() => {
    if (encounterId) {
      loadEncounter();
    }
  }, [encounterId]);

  const loadEncounter = async () => {
    try {
      const response = await api.get('/encounters', { params: { patient_id: null } });
      const enc = response.data.encounters?.find(e => e.id === encounterId);
      if (enc) setEncounter(enc);
      
      // Load existing notes
      const notesRes = await api.get('/notes', { params: { encounter_id: encounterId } });
      if (notesRes.data.notes?.length > 0) {
        const existingNote = notesRes.data.notes[0];
        setNote({
          subjective: existingNote.subjective || '',
          objective: existingNote.objective || '',
          assessment: existingNote.assessment || '',
          plan: existingNote.plan || '',
          icd_codes: existingNote.icd_codes || [],
          cpt_codes: existingNote.cpt_codes || []
        });
        setNoteId(existingNote.id);
        setAiSummary(existingNote.ai_summary || '');
      }
    } catch (error) {
      console.error('Failed to load encounter:', error);
    }
  };

  const handleSave = async () => {
    if (!encounterId) {
      toast.error('No encounter selected');
      return;
    }

    setSaving(true);
    try {
      const response = await writeResilient(
        api,
        { method: 'post', url: '/notes/soap', body: { encounter_id: encounterId, ...note } },
        {
          type: 'clinical_note_soap',
          meta: { encounterId, subject: (note.subjective || '').slice(0, 40) },
          infoMsg: 'Note saved offline — will sync when you reconnect',
        }
      );
      if (response?.data?.offline) {
        // Offline: no server-generated note_id yet — clear saving state, keep draft in UI
      } else {
        setNoteId(response.data.note_id);
        toast.success('Note saved as draft');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleAiSummarize = async () => {
    if (!noteId) {
      toast.error('Please save the note first');
      return;
    }

    setSummarizing(true);
    try {
      const response = await api.post(`/notes/${noteId}/ai-summarize`);
      setAiSummary(response.data.summary);
      toast.success('AI summary generated');
    } catch (error) {
      toast.error('AI summarization failed');
    } finally {
      setSummarizing(false);
    }
  };

  const handleSign = async () => {
    if (!noteId) {
      toast.error('Please save the note first');
      return;
    }

    try {
      await api.post(`/notes/${noteId}/sign`);
      toast.success('Note signed and locked');
    } catch (error) {
      toast.error('Failed to sign note');
    }
  };

  // Common ICD-10 codes for quick selection
  const commonICDCodes = [
    { code: 'J06.9', desc: 'Acute upper respiratory infection' },
    { code: 'E11.9', desc: 'Type 2 diabetes mellitus' },
    { code: 'I10', desc: 'Essential hypertension' },
    { code: 'M54.5', desc: 'Low back pain' },
    { code: 'F32.9', desc: 'Major depressive disorder' },
  ];

  return (
    <div className="space-y-6" data-testid="clinical-notes-editor">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#0A2342]">
        <ChevronLeft className="w-4 h-4" />
        Back to Dashboard
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Clinical Note Editor
          </h1>
          {encounter && (
            <p className="text-[#64748B] mt-1">
              {encounter.patient_name} • {encounter.encounter_type.replace('_', ' ')}
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="careiq-btn-outline flex items-center gap-2"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-note-btn"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Draft
          </Button>
          <Button 
            className="careiq-btn-secondary flex items-center gap-2"
            onClick={handleAiSummarize}
            disabled={summarizing || !noteId}
            data-testid="ai-summarize-btn"
          >
            {summarizing ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Brain className="w-4 h-4" />
            )}
            AI Summarize
          </Button>
          <Button 
            className="careiq-btn-primary flex items-center gap-2"
            onClick={handleSign}
            disabled={!noteId}
            data-testid="sign-note-btn"
          >
            <CheckCircle className="w-4 h-4" />
            Sign Note
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* SOAP Note Editor */}
        <Card className="lg:col-span-2 careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#0E7490]" />
              SOAP Note
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Subjective */}
            <div>
              <Label className="careiq-label flex items-center gap-2">
                <span className="w-6 h-6 bg-[#0E7490] text-white rounded-full flex items-center justify-center text-xs font-bold">S</span>
                Subjective
              </Label>
              <Textarea
                placeholder="Patient's chief complaint, history of present illness, symptoms described in their own words..."
                value={note.subjective}
                onChange={(e) => setNote({ ...note, subjective: e.target.value })}
                className="careiq-input min-h-[120px]"
                data-testid="note-subjective"
              />
            </div>

            {/* Objective */}
            <div>
              <Label className="careiq-label flex items-center gap-2">
                <span className="w-6 h-6 bg-[#1A56DB] text-white rounded-full flex items-center justify-center text-xs font-bold">O</span>
                Objective
              </Label>
              <Textarea
                placeholder="Physical examination findings, vital signs, lab results, imaging..."
                value={note.objective}
                onChange={(e) => setNote({ ...note, objective: e.target.value })}
                className="careiq-input min-h-[120px]"
                data-testid="note-objective"
              />
            </div>

            {/* Assessment */}
            <div>
              <Label className="careiq-label flex items-center gap-2">
                <span className="w-6 h-6 bg-[#166534] text-white rounded-full flex items-center justify-center text-xs font-bold">A</span>
                Assessment
              </Label>
              <Textarea
                placeholder="Diagnosis, clinical impression, differential diagnoses..."
                value={note.assessment}
                onChange={(e) => setNote({ ...note, assessment: e.target.value })}
                className="careiq-input min-h-[120px]"
                data-testid="note-assessment"
              />
            </div>

            {/* Plan */}
            <div>
              <Label className="careiq-label flex items-center gap-2">
                <span className="w-6 h-6 bg-[#92400E] text-white rounded-full flex items-center justify-center text-xs font-bold">P</span>
                Plan
              </Label>
              <Textarea
                placeholder="Treatment plan, medications, follow-up, referrals, patient education..."
                value={note.plan}
                onChange={(e) => setNote({ ...note, plan: e.target.value })}
                className="careiq-input min-h-[120px]"
                data-testid="note-plan"
              />
            </div>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* AI Summary */}
          <Card className="careiq-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
                <Brain className="w-5 h-5 text-[#0E7490]" />
                AI Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              {aiSummary ? (
                <div className="text-sm text-[#334155] whitespace-pre-wrap bg-[#F8FAFC] p-4 rounded-lg">
                  {aiSummary}
                </div>
              ) : (
                <p className="text-sm text-[#64748B] text-center py-4">
                  Save the note and click "AI Summarize" to generate a clinical summary.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Quick ICD Codes */}
          <Card className="careiq-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0A2342]">Quick ICD-10 Codes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {commonICDCodes.map((icd) => (
                  <button
                    key={icd.code}
                    onClick={() => {
                      if (!note.icd_codes.includes(icd.code)) {
                        setNote({ ...note, icd_codes: [...note.icd_codes, icd.code] });
                      }
                    }}
                    className={`w-full text-left p-2 rounded-lg text-sm transition-colors ${
                      note.icd_codes.includes(icd.code) 
                        ? 'bg-[#0E7490] text-white' 
                        : 'bg-[#F8FAFC] hover:bg-[#E2E8F0]'
                    }`}
                  >
                    <div className="font-medium">{icd.code}</div>
                    <div className={note.icd_codes.includes(icd.code) ? 'text-white/80' : 'text-[#64748B]'}>
                      {icd.desc}
                    </div>
                  </button>
                ))}
              </div>
              
              {note.icd_codes.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                  <Label className="text-sm text-[#64748B]">Selected Codes:</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {note.icd_codes.map((code) => (
                      <span key={code} className="px-2 py-1 bg-[#0E7490]/10 text-[#0E7490] rounded text-sm">
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
